// @ts-nocheck
import { Button } from "@/components/Button"
import { ContentLoading } from "@/components/content-loading"
import { FormCountrySelect } from "@/components/Form/CoutrySelect"
import { Input } from "@/components/Form/Input"
import { RainCorporateFormValues, useRainCorporateFormContext, VALID_EMAIL } from "@usher/pe-client-front-end-lib"
import { Fragment, useEffect } from "react"
import { Path } from "react-hook-form"
import { useHistory } from "react-router-dom"
import DateOfBirthField from "./DateOfBirthField"
import { FileUploader } from "./FileUploader"

type BusinessOfferRoute = "add-business-offer" | "add-ubo" | "add-representatives"

type BusinessOfferConfig = {
  title: string
  prefix: string
  next: string
  showUploads?: boolean
}

const FORM_CONFIG: Record<BusinessOfferRoute, BusinessOfferConfig> = {
  "add-ubo": {
    title: "Add UBO",
    prefix: "ubo",
    next: "add-representatives",
    showUploads: true,
  },
  "add-representatives": {
    title: "Add Representatives",
    prefix: "representative",
    next: "add-business-offer",
  },
  "add-business-offer": {
    title: "Add Business Officer",
    prefix: "officer",
    next: "agreement",
  },
}

const BusinessOffer = ({ type }: { type: BusinessOfferRoute }) => {
  const history = useHistory()
  const {
    verification,
    checkVerifications,
    loading,
    formMethods,
    submitLoading,
    setValid,
    submitUbo,
    submitRepresentative,
    submitOfficer,
    loadOfficer,
    loadUbo,
    loadRepresentative,
  } = useRainCorporateFormContext()
  const config = FORM_CONFIG[type] ?? FORM_CONFIG["add-business-offer"]
  useEffect(() => {
    switch (config.prefix) {
      case "ubo":
        loadUbo()
        break
      case "representative":
        loadRepresentative()
        break
      default:
        loadOfficer()
        break
    }
  }, [])
  const { control, handleSubmit } = formMethods
  const field = (name: string) => `${config.prefix}.${name}` as Path<RainCorporateFormValues>
  const onSubmit = async () => {
    switch (config.prefix) {
      case "ubo":
        try {
          await submitUbo()
          history.push("/company-verification/form/add-representatives")
          setValid((old) => ({ ...old, ubo: true }))
          await checkVerifications()
        } catch (error) {
          console.error(error)
        }
        break
      case "representative":
        try {
          await submitRepresentative()
          history.push("/company-verification/form/add-business-offer")
          setValid((old) => ({ ...old, representative: true }))
          await checkVerifications()
        } catch (error) {
          console.error(error)
        }
        break
      default:
        try {
          await submitOfficer()
          history.push("/company-verification/form/agreement")
          setValid((old) => ({ ...old, officer: true }))
          await checkVerifications()
        } catch (error) {
          console.error(error)
        }
        break
    }
  }
  return (
    <ContentLoading loading={loading}>
      <div className="">
        <h1 className="text-2xl text-black mb-4">{config.title}</h1>

        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-2 gap-2.5 mt-2.5">
            <div className="col-span-1">
              <label className="mb-1">First Name</label>
              <Input.Form name={field("first_name")} control={control} required />
            </div>
            <div className="col-span-1">
              <label className="mb-1">Last Name</label>
              <Input.Form name={field("last_name")} control={control} required />
            </div>
          </div>
          <DateOfBirthField name={field("birth_date")} control={control} required />
          <label className="mb-1">The person's national id</label>
          <Input.Form name={field("national_id")} control={control} required />
          <label className="mb-1 mt-2.5">ID Issuing Country</label>
          <FormCountrySelect name={field("country_of_issue")} control={control} required />

          <label className="mt-2.5 mb-1">ID Type</label>
          <Input.Form name={field("id_type")} required control={control} />

          {config.showUploads && (
            <Fragment>
              <label className="mt-2.5 mb-1">Upload Document</label>
              <div className="border border-gray-300 p-3">
                <FileUploader
                  type="1"
                  // @ts-ignore
                  verification={verification}
                  onSuccess={(fileName, fileId) => {
                    formMethods.setValue(field("identity_document_id"), fileId)
                    return <>{fileName}</>
                  }}
                />
              </div>
            </Fragment>
          )}

          <div className="grid sm:grid-cols-2 gap-2.5 mt-2.5 mb-1">
            <div>
              <label>Email</label>
              <Input.Form name={field("email")} control={control} required rules={VALID_EMAIL} />
            </div>
            <div>
              <label>Phone Number</label>
              <Input.Phone withFlag name={field("phone_number")} countryCodeName={field("phone_country_code")} control={control} />
            </div>
          </div>

          <label className="mt-2.5 mb-1">Address Line 1</label>
          <Input.Form name={field("address_line1")} required control={control} />
          <label className="mt-2.5 mb-1">Address Line 2</label>
          <Input.Form name={field("address_line2")} required control={control} />
          <label className="mt-2.5 mb-1">City</label>
          <Input.Form name={field("city")} required control={control} />
          <div className="grid grid-cols-2 gap-2.5 mt-2.5 mb-2.5">
            <div className="col-span-1 [&_.selector-custom]:w-full">
              <label className="mb-1">State / Province / Region</label>
              <Input.Form name={field("region")} required control={control} />
            </div>
            <div className="col-span-1">
              <label className="mb-1">Zip / Postal Code</label>
              <Input.Form name={field("postal_code")} required control={control} />
            </div>
          </div>
          <label className="mb-1">Country</label>
          <FormCountrySelect name={field("country_code")} control={control} required />

          {config.showUploads && (
            <Fragment>
              <label className="mt-2.5 mb-1">Proof of Address</label>
              <div className="border border-gray-300 p-3">
                <FileUploader
                  type="2"
                  // @ts-ignore
                  verification={verification}
                  onSuccess={(fileName, fileId) => {
                    formMethods.setValue(field("proof_of_address_id"), fileId)
                    return <>{fileName}</>
                  }}
                />
              </div>
            </Fragment>
          )}
          <div className="flex justify-end mt-4">
            <Button className="w-full" type="submit" loading={submitLoading[config.prefix as keyof typeof submitLoading] || false}>
              Continue
            </Button>
          </div>
        </form>
      </div>
    </ContentLoading>
  )
}

export default BusinessOffer
